import React, { ReactNode } from 'react';
import { useResponsive } from '../../hooks';
import { spacing, getResponsiveSpacing } from '../../utils/designTokens';

interface FormFieldProps {
  id: string;
  label: string;
  children: ReactNode;
  helpText?: string;
  required?: boolean;
  className?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  id,
  label,
  children,
  helpText,
  required = false,
  className = '',
}) => {
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');

  // Apply consistent spacing based on device
  const fieldSpacing = getResponsiveSpacing(isMobile, 'element');
  const marginClass = isMobile 
    ? spacing.mobile.margin.element 
    : spacing.desktop.margin.element;

  return (
    <div className={`${marginClass} ${className}`}>
      {isMobile ? (
        // Mobile version - stacked layout with improved spacing
        <div className="flex flex-col space-y-2">
          <label 
            htmlFor={id} 
            className="text-sm font-medium"
          >
            {label}{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="w-full">
            {children}
            {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
          </div>
        </div>
      ) : (
        // Desktop version - simpler layout that doesn't impose grid structure
        <div>
          <label 
            htmlFor={id} 
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div>
            {children}
            {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormField;